import { useState } from 'react';
import {
  Button, IconButton, Tooltip, Snackbar, Alert, Dialog, DialogTitle, DialogContent, DialogActions, Stack, TextField, MenuItem, Card, CardHeader, CardContent,
} from '@mui/material';
import { Add, Delete } from '@mui/icons-material';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { PageHeader } from '../../components/PageHeader';
import { DataTable, type Column } from '../../components/DataTable';
import { MiniBarChart } from '../../components/MiniBarChart';
import { ConfirmDialog } from '../../components/ConfirmDialog';
import { targetsApi, type Target } from '../../api/profitability.api';
import { extractApiError } from '../../api/error';
import { useAuth } from '../../hooks/useAuth';
import { useVertical } from '../../hooks/useVertical';

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
type Form = { targetType: string; userId?: number | null; stateId?: number | null; regionId?: number | null; department?: string | null; verticalId?: number | null; periodYear: number; periodMonth: number; targetValue: number };

export function TargetsPage() {
  const qc = useQueryClient();
  const { hasPermission } = useAuth();
  const { verticals, activeVertical } = useVertical();
  const year = new Date().getFullYear();
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState<Form>({ targetType: 'department', department: 'operations', verticalId: activeVertical?.id ?? null, periodYear: year, periodMonth: 1, targetValue: 0 });
  const [deleting, setDeleting] = useState<Target | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [toast, setToast] = useState<string | null>(null);

  const list = useQuery({ queryKey: ['targets', year], queryFn: () => targetsApi.list({ year, limit: 100 }) });
  const ach = useQuery({ queryKey: ['achievement', year], queryFn: () => targetsApi.achievement(year) });
  const invalidate = () => { qc.invalidateQueries({ queryKey: ['targets'] }); qc.invalidateQueries({ queryKey: ['achievement'] }); };

  const create = useMutation({ mutationFn: () => targetsApi.create(form), onSuccess: () => { setOpen(false); invalidate(); }, onError: (e) => setError(extractApiError(e)) });
  const del = useMutation({ mutationFn: (id: number) => targetsApi.remove(id), onSuccess: () => { setDeleting(null); invalidate(); }, onError: (e) => { setToast(extractApiError(e)); setDeleting(null); } });

  const inr = (n = 0) => `₹${Number(n).toLocaleString('en-IN')}`;
  const columns: Column<Target>[] = [
    { key: 'targetType', label: 'Type' },
    { key: 'scope', label: 'Scope', render: (r) => r.department ?? (r.userId ? `user#${r.userId}` : r.stateId ? `state#${r.stateId}` : r.regionId ? `region#${r.regionId}` : '—') },
    { key: 'periodMonth', label: 'Month', render: (r) => `${MONTHS[r.periodMonth - 1]} ${r.periodYear}` },
    { key: 'targetValue', label: 'Target', align: 'right', render: (r) => inr(r.targetValue) },
  ];

  const set = <K extends keyof Form>(k: K, v: Form[K]) => setForm((f) => ({ ...f, [k]: v }));
  const achData = (ach.data?.monthly ?? []).map((m) => ({ label: MONTHS[m.month - 1], value: m.pct, color: m.pct >= 100 ? '#16a34a' : '#d97706' }));

  return (
    <>
      <PageHeader title="Targets & Achievement" subtitle={`Monthly targets and achievement for ${year}.`} action={hasPermission('target.create') && <Button startIcon={<Add />} variant="contained" onClick={() => { setError(null); setOpen(true); }}>New Target</Button>} />

      <Card sx={{ mb: 2 }}>
        <CardHeader title={`Achievement % by Month — Yearly: ${ach.data?.yearly.pct ?? 0}%`} titleTypographyProps={{ variant: 'subtitle1' }} />
        <CardContent><MiniBarChart data={achData} valueFormatter={(v) => `${v}%`} /></CardContent>
      </Card>

      <DataTable
        columns={columns} rows={list.data?.data ?? []} loading={list.isLoading}
        page={1} limit={list.data?.data.length || 25} total={list.data?.meta?.total ?? 0} onPageChange={() => undefined} onLimitChange={() => undefined} rowKey={(r) => r.id}
        emptyTitle="No targets set"
        actions={(r) => (hasPermission('target.delete') ? <Tooltip title="Delete"><IconButton size="small" color="error" onClick={() => setDeleting(r)}><Delete fontSize="small" /></IconButton></Tooltip> : null)}
      />

      <Dialog open={open} onClose={() => setOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle>New Target</DialogTitle>
        <DialogContent>
          <Stack spacing={2} mt={1}>
            {error && <Alert severity="error">{error}</Alert>}
            <TextField select label="Target Type" value={form.targetType} onChange={(e) => set('targetType', e.target.value)} fullWidth>
              {['user', 'state', 'region', 'department'].map((t) => <MenuItem key={t} value={t}>{t}</MenuItem>)}
            </TextField>
            <TextField select label="Business Vertical" value={form.verticalId ?? ''} onChange={(e) => set('verticalId', e.target.value === '' ? null : Number(e.target.value))} fullWidth helperText="Leave blank for a global (cross-vertical) target">
              <MenuItem value="">Global (all verticals)</MenuItem>
              {verticals.map((v) => <MenuItem key={v.id} value={v.id}>{v.name}</MenuItem>)}
            </TextField>
            {form.targetType === 'department' && <TextField label="Department" value={form.department ?? ''} onChange={(e) => set('department', e.target.value)} fullWidth />}
            {form.targetType === 'user' && <TextField label="User ID" type="number" value={form.userId ?? ''} onChange={(e) => set('userId', Number(e.target.value))} fullWidth />}
            {form.targetType === 'state' && <TextField label="State ID" type="number" value={form.stateId ?? ''} onChange={(e) => set('stateId', Number(e.target.value))} fullWidth />}
            {form.targetType === 'region' && <TextField label="Region ID" type="number" value={form.regionId ?? ''} onChange={(e) => set('regionId', Number(e.target.value))} fullWidth />}
            <Stack direction="row" spacing={2}>
              <TextField select label="Month" value={form.periodMonth} onChange={(e) => set('periodMonth', Number(e.target.value))} fullWidth>
                {MONTHS.map((m, i) => <MenuItem key={m} value={i + 1}>{m}</MenuItem>)}
              </TextField>
              <TextField label="Year" type="number" value={form.periodYear} onChange={(e) => set('periodYear', Number(e.target.value))} fullWidth />
              <TextField label="Target Value" type="number" value={form.targetValue} onChange={(e) => set('targetValue', Number(e.target.value))} fullWidth />
            </Stack>
          </Stack>
        </DialogContent>
        <DialogActions><Button onClick={() => setOpen(false)}>Cancel</Button><Button variant="contained" disabled={!form.targetValue} onClick={() => create.mutate()}>Save</Button></DialogActions>
      </Dialog>

      <ConfirmDialog open={Boolean(deleting)} title="Delete target" message="Delete this target?" confirmLabel="Delete" onCancel={() => setDeleting(null)} onConfirm={() => deleting && del.mutate(deleting.id)} />
      <Snackbar open={Boolean(toast)} autoHideDuration={6000} onClose={() => setToast(null)}><Alert severity="warning" onClose={() => setToast(null)}>{toast}</Alert></Snackbar>
    </>
  );
}
